import {
  QuestionnaireCurrencySchema,
  type QuestionnaireCurrency,
  type QuestionnaireField,
  type QuestionnaireOption,
  type QuestionnaireSensitivity,
} from "./schema";
import { readPageDraft } from "./intake-page";
import { upgradePlanForSellerField } from "./seller-intake-defaults";
import type { IntakeAdapterField } from "./intake-adapter";
import { defaultIntakeQuestionHelp } from "./intake-copy";
export function mapIntakeField(
  field: IntakeAdapterField,
): QuestionnaireField | null {
  const base = {
    id: field.id,
    binding: field.id,
    label: field.label,
    description: defaultIntakeQuestionHelp(field.fieldKey, field.label),
    required: field.required,
    sensitivity: "standard" as QuestionnaireSensitivity,
    analyticsKey: `intake.${field.fieldKey}`,
    source: field.bindingKey
      ? {
          system: "intake-binding",
          reference: field.bindingKey,
        }
      : undefined,
  };
  const settings =
    field.settings && typeof field.settings === "object"
      ? (field.settings as Record<string, unknown>)
      : {};
  const upgrade = upgradePlanForSellerField(field);
  if (upgrade?.kind === "duration" || field.kind === "duration") {
    return { ...base, kind: "duration" };
  }
  if (upgrade?.kind === "multiple_choice") {
    return {
      ...base,
      kind: "multi_select",
      options: upgrade.options.map((option) => ({
        id: option.id,
        label: option.label,
      })),
      minSelections: 1,
      required: true,
    };
  }

  if (field.kind === "contact") {
    if (field.fieldKey === "phone" || field.bindingKey === "contact.phone") {
      return { ...base, kind: "phone" };
    }
    return {
      ...base,
      kind: "text",
      pattern:
        field.fieldKey === "email" || field.bindingKey === "contact.email"
          ? "^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$"
          : undefined,
    };
  }

  if (field.kind === "short_answer") {
    return {
      ...base,
      kind: settings.multiline === true ? "long_text" : "text",
      minLength: numberSetting(settings.minLength),
      maxLength: numberSetting(settings.maxLength),
      placeholder: stringSetting(settings.placeholder),
    };
  }

  if (field.kind === "multiple_choice") {
    const options = normalizeOptions(field.options);
    if (options.length === 0) return null;
    return field.allowMultiple
      ? {
          ...base,
          kind: "multi_select",
          options,
          minSelections: field.required ? 1 : undefined,
        }
      : { ...base, kind: "enum", options };
  }

  if (field.kind === "business_type") {
    return { ...base, kind: "business_type" };
  }

  if (
    field.kind === "industry_categories" ||
    (field.fieldKey === "industries_of_interest" &&
      field.kind === "short_answer")
  ) {
    return { ...base, kind: "industry_categories" };
  }

  if (field.kind === "yes_no") {
    return {
      ...base,
      kind: "yes_no",
      yesLabel: stringSetting(settings.yesLabel),
      noLabel: stringSetting(settings.noLabel),
    };
  }

  if (field.kind === "money") {
    const configuredCurrencies = Array.isArray(settings.currencies)
      ? settings.currencies.flatMap((item) => {
          const parsed = QuestionnaireCurrencySchema.safeParse(item);
          return parsed.success ? [parsed.data] : [];
        })
      : [];
    const currencies =
      configuredCurrencies.length > 0
        ? configuredCurrencies
        : (["USD", "CAD"] satisfies QuestionnaireCurrency[]);
    const parsedDefault = QuestionnaireCurrencySchema.safeParse(
      settings.defaultCurrency,
    );
    const defaultCurrency =
      parsedDefault.success && currencies.includes(parsedDefault.data)
        ? parsedDefault.data
        : (currencies[0] ?? "USD");
    return {
      ...base,
      kind: "money",
      currencies,
      defaultCurrency,
      minimumMinor: numberSetting(settings.minimumMinor),
      maximumMinor: numberSetting(settings.maximumMinor),
    };
  }

  if (field.kind === "page") {
    const page = readPageDraft({ label: field.label, settings });
    if (page.placement === "end" || page.enabled === false) return null;
    return {
      ...base,
      kind: "page",
      required: false,
      bullets: page.bullets,
      showTrust: page.showTrust,
      placement: page.placement,
    };
  }

  if (field.kind === "date") {
    return { ...base, kind: "date" };
  }

  if (field.kind === "file") {
    return {
      ...base,
      kind: "file",
      acceptedMimeTypes: stringArraySetting(settings.acceptedMimeTypes),
      maxFiles: numberSetting(settings.maxFiles),
      maxBytes: numberSetting(settings.maxBytes),
    };
  }

  return null;
}

function normalizeOptions(input: unknown): QuestionnaireOption[] {
  if (!Array.isArray(input)) return [];
  return input.flatMap((option, index) => {
    if (typeof option === "string" && option.trim()) {
      return [
        {
          id: slug(option) || `option_${index}`,
          label: option.trim(),
        },
      ];
    }
    if (!option || typeof option !== "object") return [];
    const record = option as Record<string, unknown>;
    if (typeof record.label !== "string" || !record.label.trim()) return [];
    return [
      {
        id:
          typeof record.id === "string" && record.id
            ? record.id
            : slug(record.label) || `option_${index}`,
        label: record.label.trim(),
        description:
          typeof record.description === "string"
            ? record.description
            : undefined,
      },
    ];
  });
}

function numberSetting(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value)
    ? value
    : undefined;
}

function stringSetting(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() ? value.trim() : undefined;
}

function stringArraySetting(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const strings = value.filter(
    (item): item is string => typeof item === "string" && Boolean(item),
  );
  return strings.length > 0 ? strings : undefined;
}

function slug(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "");
}
